import React from "react"
import { useQuery } from "@rocicorp/zero/react"

import { pro } from "@productlane/lib/src/browser"
import { queries } from "@productlane/zero/queries"

import type { SuggestionProps } from "@tiptap/suggestion"
import { useRequiredPlans } from "@/lib/plans"
import { SnippetsPopover } from "./snippets-popover"

export interface SuggestionActions {
	openSnippets: () => void
}

export interface SnippetsProps {
	withoutPortal?: boolean
	width?: number
	initialOpen?: boolean
	anchorRef?: React.RefObject<HTMLElement | null>
}

const SnippetSuggestions = React.forwardRef<
	SuggestionActions,
	React.PropsWithChildren<
		Pick<SuggestionProps, "editor"> &
			Partial<Omit<SuggestionProps, "editor">> &
			SnippetsProps
	>
>(
	(
		{
			children,
			command,
			editor,
			width,
			withoutPortal = false,
			initialOpen,
			anchorRef,
		},
		ref,
	) => {
		const [snippets] = useQuery(queries.snippets())
		const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)
		const popoverRef = React.useRef<{ setOpen: (open: boolean) => void }>(null)

		const handleCommand = React.useCallback(
			(id: string) => {
				if (!hasRequiredPlan) return
				const selectedSnippet = snippets.find((snippet) => snippet.id === id)
				if (selectedSnippet) {
					command?.({
						id: selectedSnippet.id,
						label: selectedSnippet.title,
						html: selectedSnippet.html,
					})
				}
			},
			[command, snippets, hasRequiredPlan],
		)

		React.useImperativeHandle(ref, () => ({
			openSnippets: () => {
				popoverRef.current?.setOpen(true)
			},
		}))

		if (typeof document === "undefined") {
			return null
		}

		return (
			<SnippetsPopover
				ref={popoverRef}
				withoutPortal={withoutPortal}
				width={width}
				onSelect={handleCommand}
				editor={editor}
				initialOpen={initialOpen}
				anchorRef={anchorRef}
			>
				{children}
			</SnippetsPopover>
		)
	},
)

export default SnippetSuggestions
